import { useMemo } from "react";
import { MapPin } from "lucide-react";
import { LocalizedLink } from "@/components/LocalizedLink";
import type { ExperienceWithPricing } from "@/hooks/use-experiences";
import { useUiTranslations } from "@/hooks/use-ui-translations";

interface Props {
  experiences: ExperienceWithPricing[] | undefined;
}

export function TopDestinations({ experiences }: Props) {
  const { t } = useUiTranslations();

  const destinations = useMemo(() => {
    if (!experiences) return [];
    const map = new Map<string, { count: number; image: string | null; citySlug: string | null }>();
    for (const e of experiences) {
      if (!e.location) continue;
      const existing = map.get(e.location);
      if (existing) {
        existing.count++;
      } else {
        map.set(e.location, {
          count: 1,
          image: e.images?.[0] ?? null,
          citySlug: (e as any).city_slug ?? null,
        });
      }
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 6);
  }, [experiences]);

  if (destinations.length < 2) return null;

  return (
    <section className="py-8">
      <h3 className="mb-4 text-lg font-semibold text-foreground">{t("destinations.heading")}</h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {destinations.map(([name, { count, image, citySlug }]) => {
          const slug = citySlug || name.split(",")[0].trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
          return (
            <LocalizedLink
              key={name}
              to={`/things-to-do/${slug}`}
              className="group relative overflow-hidden rounded-xl aspect-[4/3] block"
            >
              {image ? (
                <img src={image} alt={name} className="h-full w-full object-cover transition-transform group-hover:scale-105" loading="lazy" />
              ) : (
                <div className="h-full w-full bg-muted" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-0 left-0 p-3">
                <p className="flex items-center gap-1 text-sm font-semibold text-white">
                  <MapPin className="h-3.5 w-3.5" />
                  {name}
                </p>
                <p className="text-xs text-white/70">{count} {count === 1 ? "experience" : "experiences"}</p>
              </div>
            </LocalizedLink>
          );
        })}
      </div>
    </section>
  );
}
